/**
 * Centralized Cache Manager
 * 
 * Handles all cache clearing operations for:
 * - Service Worker caches
 * - Cache API caches
 * - localStorage caches (offline data, career data)
 * - React Query cache invalidation
 */

// App version for cache busting - increment on major releases
export const CACHE_VERSION = '1.4.0';

// Keys for localStorage caches (not auth tokens)
const CACHE_KEYS = [
  'offline_career_sections',
  'offline_career_roles_all',
  'ai_career_data_cache',
  'career_sections_cache',
  'career_roles_cache',
  'global_prefetch_timestamp',
  'last_prefetch_time',
] as const;

// Keys that should NOT be cleared (auth-related)
const PROTECTED_KEYS = [
  'admin_auth_token',
  'user_auth_token',
  'admin_auth_email',
  'user_auth_email',
  'admin_fresh_login_at',
  'fresh_login_grace_at', // Grace period marker for login
  'user_roles',
  'roles_fetched_at',
] as const;

/**
 * Clear all localStorage caches (preserves auth tokens)
 */
export const clearLocalStorageCaches = (): void => {
  console.log('[CacheManager] Clearing localStorage caches...');
  
  CACHE_KEYS.forEach(key => {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.warn(`[CacheManager] Failed to remove ${key}:`, e);
    }
  });
  
  // Also clear any dynamic cache keys with patterns
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (
      key.startsWith('offline_') ||
      key.startsWith('cache_') ||
      key.startsWith('prefetch_') ||
      key.includes('_cache')
    ) && !PROTECTED_KEYS.includes(key as any)) {
      keysToRemove.push(key);
    }
  }
  
  keysToRemove.forEach(key => {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.warn(`[CacheManager] Failed to remove ${key}:`, e);
    }
  });
  
  console.log(`[CacheManager] Cleared ${CACHE_KEYS.length + keysToRemove.length} localStorage items`);
};

/**
 * Clear all Cache API caches
 */
export const clearCacheAPICaches = async (): Promise<void> => {
  if (!('caches' in window)) {
    console.log('[CacheManager] Cache API not available');
    return;
  }
  
  try {
    const cacheNames = await caches.keys();
    console.log('[CacheManager] Clearing Cache API caches:', cacheNames);
    
    await Promise.all(
      cacheNames.map(name => caches.delete(name))
    );
    
    console.log(`[CacheManager] Cleared ${cacheNames.length} Cache API caches`);
  } catch (e) {
    console.error('[CacheManager] Failed to clear Cache API caches:', e);
  }
};

/**
 * Unregister all service workers and force update
 */
export const unregisterServiceWorkers = async (): Promise<void> => {
  if (!('serviceWorker' in navigator)) {
    console.log('[CacheManager] Service Worker not available');
    return;
  }
  
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    console.log('[CacheManager] Unregistering service workers:', registrations.length);
    
    await Promise.all(
      registrations.map(async (registration) => {
        // Try to update first, then unregister
        try {
          await registration.update();
        } catch (e) {
          // Ignore update errors
        }
        return registration.unregister();
      })
    );
    
    console.log('[CacheManager] All service workers unregistered');
  } catch (e) {
    console.error('[CacheManager] Failed to unregister service workers:', e);
  }
};

/**
 * Force update service workers without unregistering
 */
export const forceServiceWorkerUpdate = async (): Promise<void> => {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(
      registrations.map(async (r) => {
        // Skip if SW is in an invalid state (installing/redundant/no active worker)
        if (!r.active || r.installing) {
          return;
        }
        try {
          await r.update();
        } catch (err) {
          // InvalidStateError and similar are non-fatal — log quietly
          console.debug('[CacheManager] SW update skipped:', (err as Error)?.message);
        }
      })
    );
  } catch (e) {
    console.debug('[CacheManager] Failed to enumerate service workers:', (e as Error)?.message);
  }
};

/**
 * Clear all caches and reload the page
 * Use this for a complete fresh start
 */
export const clearAllCachesAndReload = async (): Promise<void> => {
  console.log('[CacheManager] Starting complete cache clear...');
  
  // 1. Clear localStorage caches
  clearLocalStorageCaches();
  
  // 2. Clear Cache API caches
  await clearCacheAPICaches();
  
  // 3. Unregister service workers
  await unregisterServiceWorkers();
  
  // 4. Update version
  localStorage.setItem('app_version', CACHE_VERSION);
  
  console.log('[CacheManager] All caches cleared, reloading...');
  
  // 5. Force hard reload bypassing cache
  window.location.reload();
};

/**
 * Clear caches on successful login
 * Less aggressive than clearAllCachesAndReload - preserves auth
 */
export const clearCachesOnLogin = async (): Promise<void> => {
  console.log('[CacheManager] Clearing caches after login...');
  
  // 1. Clear localStorage data caches (not auth)
  clearLocalStorageCaches();
  
  // 2. Clear Cache API caches
  await clearCacheAPICaches();
  
  // 3. Force service worker update (don't unregister)
  await forceServiceWorkerUpdate();
  
  // 4. Update version
  localStorage.setItem('app_version', CACHE_VERSION);
  
  console.log('[CacheManager] Login cache clear complete');
};

/**
 * Clear all caches on sign out
 * Most aggressive - removes everything including auth
 */
export const clearCachesOnSignOut = async (): Promise<void> => {
  console.log('[CacheManager] Clearing all caches on sign out...');
  
  // 1. Clear all localStorage (including auth this time)
  clearLocalStorageCaches();
  
  // Also clear auth tokens explicitly
  PROTECTED_KEYS.forEach(key => {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      // Ignore
    }
  });
  
  // 2. Clear sessionStorage completely
  try {
    sessionStorage.clear();
  } catch (e) {
    console.warn('[CacheManager] Failed to clear sessionStorage:', e);
  }
  
  // 3. Clear Cache API caches
  await clearCacheAPICaches();
  
  // 4. Unregister service workers
  await unregisterServiceWorkers();
  
  console.log('[CacheManager] Sign out cache clear complete');
};

/**
 * Check if app version has changed and clear caches if needed
 * Call this on app initialization
 */
export const checkVersionAndClearCaches = async (): Promise<boolean> => {
  const storedVersion = localStorage.getItem('app_version');
  
  if (storedVersion !== CACHE_VERSION) {
    console.log(`[CacheManager] Version changed: ${storedVersion} -> ${CACHE_VERSION}`);
    
    // Clear caches but don't reload
    clearLocalStorageCaches();
    await clearCacheAPICaches();
    await forceServiceWorkerUpdate();
    
    // Update version
    localStorage.setItem('app_version', CACHE_VERSION);
    
    return true; // Version changed, caches cleared
  }
  
  return false; // No change
};

/**
 * Clear stale assets when new version is detected
 * More aggressive cleanup for cache mismatches
 */
export const clearStaleAssets = async (): Promise<void> => {
  if (!('caches' in window)) return;
  
  try {
    const cacheNames = await caches.keys();
    
    // Delete all workbox/precache related caches
    const staleCaches = cacheNames.filter(name => 
      name.includes('workbox') || 
      name.includes('precache') ||
      name.includes('career-path') ||
      name.includes('runtime')
    );
    
    if (staleCaches.length > 0) {
      console.log('[CacheManager] Clearing stale asset caches:', staleCaches);
      await Promise.all(staleCaches.map(name => caches.delete(name)));
    }
  } catch (e) {
    console.error('[CacheManager] Failed to clear stale assets:', e);
  }
};

/**
 * Setup automatic cache invalidation on page load
 * Detects asset loading failures and clears stale caches
 */
export const setupAssetLoadingErrorHandler = (): void => {
  // Listen for resource loading errors
  window.addEventListener('error', async (event) => {
    const target = event.target as HTMLElement;
    
    // Check if it's a script or link (CSS) loading error
    if (target instanceof HTMLScriptElement || target instanceof HTMLLinkElement) {
      const src = target instanceof HTMLScriptElement ? target.src : (target as HTMLLinkElement).href;
      
      // Check if it's an asset loading failure (likely stale cache)
      if (src && (src.includes('/assets/') || src.includes('.js') || src.includes('.css'))) {
        console.error('[CacheManager] Asset loading failed, clearing stale caches:', src);
        
        // Mark that we need recovery
        sessionStorage.setItem('asset_load_failure', 'true');
        
        // Clear stale assets and reload
        await clearStaleAssets();
        await unregisterServiceWorkers();
        
        // Reload once (prevent infinite loop)
        if (!sessionStorage.getItem('recovery_attempted')) {
          sessionStorage.setItem('recovery_attempted', 'true');
          window.location.reload();
        }
      }
    }
  }, true); // Use capture phase
  
  // Clear recovery flag on successful load
  window.addEventListener('load', () => {
    sessionStorage.removeItem('asset_load_failure');
    sessionStorage.removeItem('recovery_attempted');
  });
};

/**
 * Initialize cache management on app start
 */
export const initializeCacheManagement = async (): Promise<void> => {
  // Setup error handler for asset loading failures
  setupAssetLoadingErrorHandler();
  
  // Check version and clear if needed
  const versionChanged = await checkVersionAndClearCaches();
  
  if (versionChanged) {
    console.log('[CacheManager] Caches cleared due to version change');
  }
};
